import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Package,
  Calendar,
  CheckCircle,
  Clock,
  FileSpreadsheet,
  FileText,
} from "lucide-react";
import MetricCard from "../components/MetricCard";
import OrdersTable from "../components/OrdersTable";
import { exportClinicSummariesToExcel, exportClinicSummariesToPDF } from "../lib/exportUtils";
import { APIURL, TZ } from "../../Global";
import "../styles.css";

export const OrdersPanel = () => {
  const navigate = useNavigate();
  const [orderStats, setOrderStats] = useState(null);
  const [clinicOrders, setClinicOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [dateFilterType, setDateFilterType] = useState("current_month"); // today | current_month | last_month | custom
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [selectedClinicIds, setSelectedClinicIds] = useState([]);

  const getDateRangeForFilter = (type, start, end) => {
    const formatISODate = (date) => date.toISOString().split("T")[0];

    const today = new Date();
    let startDate = "";
    let endDate = "";

    if (type === "today") {
      const d = formatISODate(today);
      startDate = d;
      endDate = d;
    } else if (type === "current_month") {
      const first = new Date(today.getFullYear(), today.getMonth(), 1);
      const last = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      startDate = formatISODate(first);
      endDate = formatISODate(last);
    } else if (type === "last_month") {
      const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const last = new Date(today.getFullYear(), today.getMonth(), 0);
      startDate = formatISODate(first);
      endDate = formatISODate(last);
    } else if (type === "custom" && start && end) {
      startDate = start;
      endDate = end;
    }

    return { startDate, endDate };
  };

  const handleFilterTypeChange = (nextType) => {
    if (nextType === "custom") {
      const { startDate, endDate } = getDateRangeForFilter(
        dateFilterType,
        customStartDate,
        customEndDate,
      );
      if (startDate && endDate) {
        setCustomStartDate(startDate);
        setCustomEndDate(endDate);
      }
    }
    setDateFilterType(nextType);
  };

  const formatClinicName = (name) => {
    if (!name) return "—";
    return name
      .toString()
      .toLowerCase()
      .split(" ")
      .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : ""))
      .join(" ");
  };

  useEffect(() => {
    const fetchDashboardData = async (pageToLoad = 1, append = false) => {
      if (append) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
        setClinicOrders([]);
      }
      setApiError(null);

      try {
        const tokenString = sessionStorage.getItem("usertoken");
        const token = tokenString ? tokenString.replace(/["]+/g, "") : null;
        const headers = token ? { Authorization: `Token ${token}` } : {};
        const { startDate, endDate } = getDateRangeForFilter(
          dateFilterType,
          customStartDate,
          customEndDate,
        );
        const dateQuery =
          startDate && endDate ? `&start_date=${startDate}&end_date=${endDate}` : "";
        const searchQuery = searchTerm ? `&search=${encodeURIComponent(searchTerm.trim())}` : "";

        const { data } = await axios.get(
          `${APIURL}/api/v1/service-provider/central-dashboard/?${TZ}${dateQuery}${searchQuery}&page=${pageToLoad}`,
          {
            headers,
          },
        );

        setOrderStats(data?.order_details?.[0] || null);
        const newClinics = data?.clinic_orders || [];
        setClinicOrders((prev) => (append ? [...prev, ...newClinics] : newClinics));
        setHasNext(Boolean(data?.has_next));
        const nextPage = (data?.page ?? pageToLoad) + 1;
        setPage(nextPage);
      } catch (error) {
        console.error("Failed to load pharmacy dashboard data", error);
        setApiError("Unable to load order metrics right now.");
      } finally {
        if (append) {
          setIsLoadingMore(false);
        } else {
          setIsLoading(false);
        }
      }
    };

    fetchDashboardData(1, false);
  }, [dateFilterType, customStartDate, customEndDate, searchTerm]);

  const metrics = {
    totalOrders: orderStats?.total_order ?? 0,
    totalOrdersToday: orderStats?.total_order_today ?? 0,
    totalOrdersThisMonth: orderStats?.total_order_this_month ?? 0,
    totalOrdersLastMonth: orderStats?.total_order_last_month ?? 0,
    percentChange: orderStats?.percent_increased ?? 0,
    // API uses singular keys: completed_order, pending_order
    completedOrders: orderStats?.completed_order ?? 0,
    pendingOrders: orderStats?.pending_order ?? 0,
    rejectedOrders: orderStats?.rejected_order ?? 0,
  };

  const clinicSummaries = useMemo(
    () =>
      clinicOrders.map((clinic) => ({
        clinicId: clinic.hospital_id,
        clinicName: formatClinicName(clinic.hospital_name),
        location: clinic.location || clinic.city || clinic.country || "—",
        mobileNumber: clinic.mobile_number || "",
        totalOrders: clinic.order_count ?? 0,
      })),
    [clinicOrders],
  );

  const selectedClinicSummaries = useMemo(() => {
    if (!selectedClinicIds.length) {
      return clinicSummaries;
    }
    const selectedSet = new Set(selectedClinicIds);
    return clinicSummaries.filter((clinic) => selectedSet.has(clinic.clinicId));
  }, [clinicSummaries, selectedClinicIds]);

  const handleToggleClinicSelection = (clinicId) => {
    setSelectedClinicIds((prev) =>
      prev.includes(clinicId) ? prev.filter((id) => id !== clinicId) : [...prev, clinicId],
    );
  };

  const handleToggleSelectAll = () => {
    if (selectedClinicIds.length === clinicSummaries.length) {
      setSelectedClinicIds([]);
    } else {
      setSelectedClinicIds(clinicSummaries.map((clinic) => clinic.clinicId));
    }
  };

  return (
    <>
      <div className="pharmacy-page-header">
        <h1 className="pharmacy-page-title">Order Management</h1>
        <p className="pharmacy-page-subtitle">
          Track and manage clinic orders across all locations
        </p>
      </div>

      <div className="pharmacy-card-grid">
        <MetricCard
          title="Today's Orders"
          value={metrics.totalOrdersToday}
          icon={Calendar}
          trend={`${new Date().toLocaleDateString("en-GB")} `}
          backgroundColor="#ede8ff"
          iconBackground="#ffffff"
          trendUp
        />
        <MetricCard
          title="Total Orders"
          value={metrics.totalOrders}
          icon={Package}
          // trend={`${metrics.totalOrders} total orders`}
          trendUp
        />
       
        <MetricCard
          title="Completed Orders"
          value={metrics.completedOrders}
          icon={CheckCircle}
          trend={
            <span style={{ color: "#d92d20" }}>
              {metrics.rejectedOrders} rejected
            </span>
          }
        />
        <MetricCard
          title="Pending Orders"
          value={metrics.pendingOrders}
          icon={Clock}
          // trend={`${metrics.pendingOrders} pending`}
        />
      </div>

      <div className="pharmacy-panel">
        <div className="pharmacy-panel-header">
          <h2 className="pharmacy-panel-title">Clinic Orders</h2>
          <div className="pharmacy-action-group">
            <button
              type="button"
              className="pharmacy-button"
              onClick={handleToggleSelectAll}
            >
              {selectedClinicIds.length === clinicSummaries.length && clinicSummaries.length > 0
                ? "Clear Selection"
                : "Select All"}
            </button>
            <button
              type="button"
              className="pharmacy-button"
              onClick={() => exportClinicSummariesToExcel(selectedClinicSummaries)}
            >
              <FileSpreadsheet className="pharmacy-button-icon" />
              Export Excel
            </button>
            <button
              type="button"
              className="pharmacy-button"
              onClick={() => exportClinicSummariesToPDF(selectedClinicSummaries)}
            >
              <FileText className="pharmacy-button-icon" />
              Export PDF
            </button>
          </div>
        </div>
        {apiError && (
          <div style={{ marginBottom: 16, color: "#d92d20" }}>
            {apiError}
          </div>
        )}

        <OrdersTable
          clinicSummaries={clinicSummaries}
          selectedClinicIds={selectedClinicIds}
          onToggleClinicSelection={handleToggleClinicSelection}
          dateFilterType={dateFilterType}
          onFilterTypeChange={handleFilterTypeChange}
          customStartDate={customStartDate}
          customEndDate={customEndDate}
          onCustomStartDateChange={setCustomStartDate}
          onCustomEndDateChange={setCustomEndDate}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          hasNext={hasNext}
          isLoadingMore={isLoadingMore}
          onLoadMore={() => {
            if (hasNext && !isLoadingMore) {
              // load next page and append
              (async () => {
                const tokenString = sessionStorage.getItem("usertoken");
                const token = tokenString ? tokenString.replace(/["]+/g, "") : null;
                const headers = token ? { Authorization: `Token ${token}` } : {};
                const { startDate, endDate } = getDateRangeForFilter(
                  dateFilterType,
                  customStartDate,
                  customEndDate,
                );
                const dateQuery =
                  startDate && endDate ? `&start_date=${startDate}&end_date=${endDate}` : "";
                const searchQuery = searchTerm
                  ? `&search=${encodeURIComponent(searchTerm.trim())}`
                  : "";

                setIsLoadingMore(true);
                try {
                  const { data } = await axios.get(
                    `${APIURL}/api/v1/service-provider/central-dashboard/?${TZ}${dateQuery}${searchQuery}&page=${page}`,
                    { headers },
                  );
                  const newClinics = data?.clinic_orders || [];
                  setClinicOrders((prev) => [...prev, ...newClinics]);
                  setHasNext(Boolean(data?.has_next));
                  const nextPage = (data?.page ?? page) + 1;
                  setPage(nextPage);
                } catch (error) {
                  console.error("Failed to load more clinic orders", error);
                } finally {
                  setIsLoadingMore(false);
                }
              })();
            }
          }}
          onRowClick={(clinicId) => {
            if (clinicId) {
              const { startDate, endDate } = getDateRangeForFilter(
                dateFilterType,
                customStartDate,
                customEndDate,
              );
              const params = new URLSearchParams();
              if (startDate) params.set("start_date", startDate);
              if (endDate) params.set("end_date", endDate);
              const search = params.toString();
              navigate(
                `/pharmacy/orders/clinics/${clinicId}${search ? `?${search}` : ""}`,
              );
            }
          }}
        />

        {isLoading && (
          <div style={{ padding: "12px 0", textAlign: "center" }}>
            Loading clinic orders...
          </div>
        )}

        {isLoadingMore && (
          <div style={{ padding: "12px 0", textAlign: "center" }}>
            Loading more clinics...
          </div>
        )}
      </div>
    </>
  );
};

const Orders = () => (
  <div className="pharmacy-page">
    <div className="pharmacy-container">
      <OrdersPanel />
    </div>
  </div>
);

export default Orders;

